import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Banner, Button, Icon, Modal, Tag, Toggle } from "@/shared/rd";
import type { AppConfigView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { defaultFor, effectiveValue, formatDate, formatValue, isChanged, isOn } from "@/shared/app-configs/lib/configValue";
import { useConfigWrites } from "@/shared/app-configs/components/useConfigWrites";

type Props = {
  config: AppConfigView;
  /** The first row in its card draws no top divider. */
  first?: boolean;
  /** Opens the key's details drawer (value, source, history). */
  onOpen: (key: string) => void;
};

export const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * One key as a compact row: its name (opens the details), the Needs restart /
 * Custom tags, description and technical key; then "Changed" when a value is
 * stored here, and the value itself — a switch for on/off keys, the current
 * value for everything else; "Changed" sits after the name. The whole row opens the details drawer, where
 * the value is edited; the name stays the row's keyboard-focusable button.
 *
 * A live switch flips at once and offers Undo; a start-up switch only takes
 * effect after a restart, so it asks first. A failed flip shows an error
 * banner above the row with Try again.
 */
export function AppConfigRow({ config, first = false, onOpen }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const name = (locale === "ar" ? config.nameAr : config.nameEn) || config.key;
  const description = locale === "ar" ? config.descriptionAr : config.descriptionEn;
  const writes = useConfigWrites(config, name);
  // The switch's position while its write is in flight (or awaiting confirm).
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const isBoolean = config.type === "boolean";
  const isCustom = config.origin === "custom";
  const needsRestart = config.scope === "boot";
  const current = effectiveValue(config);
  const shown = optimistic ?? current;
  const changed = isChanged(config);

  async function write(value: string, undo: boolean) {
    setOptimistic(value);
    await writes.save(value, { undo });
    // Success refetched the list; a refusal puts the switch back where the server has it.
    setOptimistic(null);
  }

  return (
    <div style={{ borderTop: first ? undefined : "1px solid var(--rd-line-row)" }}>
      {writes.failure ? (
        <div style={{ padding: "12px 16px 0" }}>
          <Banner
            tone="error"
            title={t("appConfigs.saveFailedTitle", { name })}
            actions={
              <Button size="sm" loading={writes.isPending} loadingLabel={t(keys.shell.retrying)} onClick={() => void writes.failure?.retry()}>
                {t(keys.shell.retry)}
              </Button>
            }
            onDismiss={writes.clearFailure}
            dismissLabel={t(keys.common.close)}
          >
            {`${writes.failure.message} ${t("appConfigs.nothingChanged")}`}
          </Banner>
        </div>
      ) : null}
      <div
        className="rd-row-link"
        style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 16px" }}
        // A click anywhere on the row opens the details, except on the switch:
        // it has its own job. Keyboard users reach the same drawer through
        // the name, which is the row's real button.
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("button, [role=switch], a, input")) return;
          onOpen(config.key);
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              dir="auto"
              onClick={() => onOpen(config.key)}
              title={t("appConfigs.detailsTip")}
              style={{ border: 0, padding: 0, background: "transparent", font: "inherit", fontWeight: 500, color: "var(--rd-text)", textAlign: "start", cursor: "pointer" }}
            >
              {name}
            </button>
            {needsRestart ? (
              <Tag tone="amber" title={t("appConfigs.needsRestartTip")}>
                {t("appConfigs.needsRestart")}
              </Tag>
            ) : null}
            {isCustom ? <Tag tone="purple">{t(keys.appConfigs.originCustom)}</Tag> : null}
            {changed ? (
              <span
                title={
                  config.updatedAt
                    ? t("appConfigs.changedTip", { date: formatDate(config.updatedAt, locale), value: formatValue(config, defaultFor(config), t) })
                    : undefined
                }
                style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--rd-primary)", whiteSpace: "nowrap" }}
              >
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--rd-primary)" }} />
                {t("appConfigs.changed")}
              </span>
            ) : null}
          </span>
          {description ? (
            <span dir="auto" style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
              {description}
            </span>
          ) : null}
          <span dir="ltr" style={{ fontFamily: MONO, fontSize: 12, color: "var(--rd-text-meta)", textAlign: "start", overflowWrap: "anywhere" }}>
            {config.key}
          </span>
        </div>


        {isBoolean ? (
          <Toggle
            checked={isOn(shown)}
            disabled={writes.isPending}
            label={name}
            onChange={(next) => {
              const value = next ? "true" : "false";
              if (needsRestart) setConfirming(value);
              else void write(value, true);
            }}
          />
        ) : (
          <>
            <span
              dir="ltr"
              title={formatValue(config, current, t)}
              style={{
                maxWidth: 240,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "3px 8px",
                borderRadius: "var(--rd-radius-md)",
                background: "var(--rd-surface-sunken)",
                fontFamily: MONO,
                fontSize: 12,
                color: "var(--rd-text-secondary)",
              }}
            >
              {formatValue(config, current, t)}
            </span>
          </>
        )}
        <Icon icon={ChevronRight} size={16} color="var(--rd-text-meta)" className="rd-flip-rtl" />
      </div>

      {/* A start-up switch: the change waits for a restart, so it is confirmed rather than undone. */}
      <Modal
        open={confirming != null}
        onClose={writes.isPending ? undefined : () => setConfirming(null)}
        width={440}
        title={t("appConfigs.changeTitle", { name })}
        actions={
          <>
            <Button variant="ghost" muted onClick={() => setConfirming(null)} disabled={writes.isPending}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant="primary"
              loading={writes.updatePending}
              loadingLabel={t("appConfigs.applying")}
              onClick={async () => {
                if (confirming == null) return;
                await write(confirming, false);
                setConfirming(null);
              }}
            >
              {t(keys.appConfigs.confirmAction)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {confirming != null
            ? t("appConfigs.changeBodyRestart", { from: formatValue(config, current, t), to: formatValue(config, confirming, t) })
            : null}
        </p>
      </Modal>
    </div>
  );
}
